/**
 * Portfolio / search card cover aspect — keep in sync with:
 * - `PortfolioContent` / `ProjectCard` `aspectRatio` (`16:9` or `1:1`)
 * - `Thumbnail` padding ratio for `'16:9'` (56.25%) and `'1:1'` (100%)
 * - API project thumbs (`720×405` in `apps/api/config/thumbs.php`)
 *
 * Image and video covers are always 16:9. Audio custom cover art may be
 * cropped square (1:1) or widescreen (16:9); media still derives both wide
 * and square thumbnails from the single crop master (the non-selected aspect
 * is a secondary centre crop of that master). Generated audio cards (no
 * custom cover) remain 16:9 waveform cards. Custom audio covers display as
 * square on portfolio cards and the project page (album-style).
 */
export const PROJECT_CARD_ASPECT_RATIO = 16 / 9;
export const PROJECT_CARD_ASPECT_LABEL = '16:9';

/** Audio cover-art crop choice (album-style). */
export const PROJECT_CARD_ASPECT_SQUARE = 1;
export const PROJECT_CARD_ASPECT_SQUARE_LABEL = '1:1';

/** In-form live card preview width (px). */
export const PROJECT_CARD_PREVIEW_WIDTH = 320;

/** Rendered thumbnail at 2× the ~320×180 card image area. */
export const PROJECT_CARD_THUMB_2X_WIDTH = 640;
export const PROJECT_CARD_THUMB_2X_HEIGHT = 360;

/**
 * Output size for a rendered cover preview at the given crop aspect.
 * Square masters use a square canvas; everything else uses the 16:9 2× size.
 *
 * @param {number} aspectRatio width / height
 * @returns {{ width: number, height: number }}
 */
export function getCoverPreviewOutputSize(aspectRatio) {
  if (aspectRatio === PROJECT_CARD_ASPECT_SQUARE || aspectRatio === 1) {
    return {
      width: PROJECT_CARD_THUMB_2X_HEIGHT,
      height: PROJECT_CARD_THUMB_2X_HEIGHT,
    };
  }

  return {
    width: PROJECT_CARD_THUMB_2X_WIDTH,
    height: PROJECT_CARD_THUMB_2X_HEIGHT,
  };
}

/**
 * Infer whether a crop box is closer to square or 16:9.
 * Used when re-opening Adjust thumbnail for an existing audio cover.
 *
 * @param {number} width
 * @param {number} height
 * @returns {number} PROJECT_CARD_ASPECT_SQUARE or PROJECT_CARD_ASPECT_RATIO
 */
export function inferCoverAspectRatio(width, height) {
  if (
    !width ||
    !height ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    height <= 0
  ) {
    return PROJECT_CARD_ASPECT_RATIO;
  }

  const ratio = width / height;
  const distSquare = Math.abs(ratio - PROJECT_CARD_ASPECT_SQUARE);
  const distWide = Math.abs(ratio - PROJECT_CARD_ASPECT_RATIO);

  return distSquare <= distWide
    ? PROJECT_CARD_ASPECT_SQUARE
    : PROJECT_CARD_ASPECT_RATIO;
}

/**
 * Default crop aspect when adding cover art for a media type.
 * Audio defaults to square (album-style); image/video stay 16:9.
 *
 * @param {string|null|undefined} mediaType
 * @returns {number}
 */
export function defaultCoverAspectForMedia(mediaType) {
  return mediaType === 'audio'
    ? PROJECT_CARD_ASPECT_SQUARE
    : PROJECT_CARD_ASPECT_RATIO;
}

/**
 * True when a project should render its cover in a square frame.
 * Custom audio cover art (`thumbnails.generated === false`) uses album-style
 * square framing; generated waveform cards and all other media stay 16:9.
 *
 * @param {{ mediaType?: string, thumbnails?: { generated?: boolean|null } }|null|undefined} project
 * @returns {boolean}
 */
export function preferSquareProjectCover(project) {
  if (!project || project.mediaType !== 'audio') {
    return false;
  }

  return project.thumbnails && project.thumbnails.generated === false;
}

/**
 * Aspect-ratio label for `Thumbnail` / `ProjectCard` for a project.
 *
 * @param {{ mediaType?: string, thumbnails?: { generated?: boolean|null } }|null|undefined} project
 * @returns {'1:1'|'16:9'}
 */
export function projectCoverAspectRatioLabel(project) {
  return preferSquareProjectCover(project)
    ? PROJECT_CARD_ASPECT_SQUARE_LABEL
    : PROJECT_CARD_ASPECT_LABEL;
}
